import { action, makeObservable, observable, runInAction } from 'mobx';

/**
 * IU-10 (`docs/concept-iphone-ux.md#iu-10-create-title-first-on-the-sheet-primitive`, F-0510):
 * "an added record appears with a toast. After Add, the sheet closes, the new row is in the list
 * with the insertion motion, and the toast reads *Added I-0123*", with **View** (I-1190: the item's
 * state is the list row's to show, as its chip — a bare "Open" beside a button read as a verb).
 *
 * Held outside `WorkItemCreatePage`'s own component tree for the same reason
 * `ItemArchiveToastStore` and `DecisionAnswerToastStore` are: Add itself navigates away from the
 * page that showed the toast's trigger (back to the list, not the sheet), so the toast has to live
 * somewhere that survives it. Mounted once (`ItemCreatedToast`, in `Layout.tsx`). A single active
 * toast, the same rule the other two follow: creating a second item while one toast shows replaces
 * it rather than stacking.
 */
export interface IItemCreatedToastState {
  readonly itemId: string;
  readonly itemKey: string;
  readonly expiresAt: number;
}

/** The same window the other two IU toasts show for (M-8). */
export const ITEM_CREATED_TOAST_MS = 8_000;

export class ItemCreatedToastStore {
  toast: IItemCreatedToastState | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeObservable(this, {
      toast: observable.ref,
      show: action,
      dismiss: action,
    });
  }

  show(itemId: string, itemKey: string): void {
    if (this.timer) clearTimeout(this.timer);
    this.toast = { itemId, itemKey, expiresAt: Date.now() + ITEM_CREATED_TOAST_MS };
    this.timer = setTimeout(() => runInAction(() => this.dismiss()), ITEM_CREATED_TOAST_MS);
  }

  dismiss(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.toast = null;
  }

  /** Tests only. */
  resetForTests(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.toast = null;
  }
}
